import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A bubble chart is a data visualization tool that displays three dimensions of data using
          bubbles on a two-dimensional graph. Each bubble represents a data point, with its position
          on the X and Y axes corresponding to two variables. The size of the bubble indicates the
          value of a third variable, allowing users to compare and identify patterns across multiple
          dimensions. Bubble charts are particularly useful for visualizing the relationships
          between three continuous variables, such as growth, demographics, and success rates, in a
          visually intuitive and engaging way. This chart type helps reveal correlations, trends,
          and outliers in complex data sets.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Bubble charts are an effective way to visualize relationships between variables using size
          and position for clarity. Here are three key uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Representing Multi-Variable Data:</strong> They showcase relationships between
            three dimensions, with x and y axes for two variables and bubble size for the third.
          </li>
          <li>
            <strong>Highlighting Outliers:</strong> The size and placement of bubbles make it easy
            to identify outliers or anomalies in the dataset.
          </li>
          <li>
            <strong>Comparing Proportions:</strong> Bubble charts effectively demonstrate
            proportional differences between data points, making them useful for demographic or
            market analysis.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Bubble charts offer a dynamic way to represent relationships between three variables, but
          they are not without challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Size Perception Issues:</strong> Human perception struggles with accurately
            comparing the areas of circles, especially when their size differences are subtle. This
            can lead to misinterpretation of data magnitude.
          </li>
          <li>
            <strong>Overlapping Bubbles:</strong> In dense datasets, bubbles often overlap, making
            it difficult to discern individual data points. This limits the chart&apos;s clarity and
            usability for large or complex datasets.
          </li>
          <li>
            <strong>Limited Axes Representation:</strong> While bubble charts can represent three
            variables (x, y, and size), adding more dimensions, such as color or grouping, can
            overwhelm viewers and reduce interpretability.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in bubble charts is essential for clarity and effective data
          communication. Distinct, high-contrast colors can differentiate between categories, while
          gradient shades can represent varying values such as magnitude or intensity. Transparent
          or semi-opaque fills ensure overlapping bubbles remain visible without obscuring data.
          Careful color choices help guide focus, minimize visual clutter, and enhance the overall
          interpretability of the chart.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Bubble charts are a popular choice for visualizing three dimensions of data, but they come
          with significant challenges that can limit their effectiveness:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Size Perception Issues:</strong> The use of bubble area to represent values can
            be misleading, as human perception is not linear. Viewers often struggle to accurately
            compare bubble sizes, leading to potential misinterpretation of data.
          </li>
          <li>
            <strong>Overlapping Bubbles:</strong> When bubbles overlap, especially in dense
            datasets, it obscures information and makes it difficult to discern individual values or
            compare data points effectively.
          </li>
          <li>
            <strong>Limited Scalability:</strong> Bubble charts work best for small to medium
            datasets. Large datasets can result in cluttered visuals, where bubbles lose their
            individual significance, reducing the chart&apos;s overall readability.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
